import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Product {
  title: string;
  editionLabel?: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl?: string;
}
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: Product;
}
// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------
const NAMES = ['María', 'Sofía', 'Lucía', 'Camila', 'Valentina', 'Martina', 'Florencia', 'Agustina', 'Julieta', 'Catalina', 'Renata', 'Emilia', 'Victoria', 'Carolina', 'Antonella', 'Belén', 'Daniela', 'Gabriela', 'Paula', 'Romina', 'Micaela', 'Ana', 'Laura', 'Natalia', 'Pilar', 'Rocío', 'Bianca', 'Aldana', 'Brenda', 'Tamara', 'Ailín', 'Mariana', 'Constanza', 'Delfina', 'Eugenia', 'Fernanda', 'Guadalupe', 'Ivana', 'Jazmín', 'Karen'];
const CITIES = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'San Miguel de Tucumán', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Resistencia', 'Neuquén', 'Posadas', 'Bahía Blanca', 'Paraná', 'Formosa', 'San Luis', 'La Rioja', 'Río Cuarto', 'Comodoro Rivadavia', 'Ushuaia', 'Bariloche', 'Tandil', 'Pilar', 'Quilmes', 'Avellaneda', 'San Isidro', 'Tigre', 'Lomas de Zamora'];
const FALLBACK_PRODUCTS: {
  product: Product;
  weight: number;
}[] = [{
  product: {
    title: 'Protocolo Anti-Inflamación',
    editionLabel: 'Plan 30 días + Reset 72hs',
    priceNumber: 25000,
    priceText: '$25.000 ARS'
  },
  weight: 70
}, {
  product: {
    title: 'Protocolo Anti-Inflamación + Bonos',
    editionLabel: 'Pack Completo',
    priceNumber: 37500,
    priceText: '$37.500 ARS'
  },
  weight: 30
}];
// ---------------------------------------------------------------------------
// Page scanner — extracts products + prices from the DOM
// ---------------------------------------------------------------------------
function parsePrice(text: string): {
  num: number;
  raw: string;
} | null {
  if (!text) return null;
  // Match $X.XXX (AR format), $X,XXX, $X.XX, or USD/ARS prefixed values
  const arRegex = /\$\s?(\d{1,3}(?:\.\d{3})+(?:,\d{2})?)(?!\s*\d)/;
  const usRegex = /\$\s?(\d+(?:,\d{3})*(?:\.\d{2})?)\b/;
  const simpleRegex = /\$\s?(\d+(?:[.,]\d+)*)/;
  let match = text.match(arRegex);
  if (match) {
    const raw = match[0].trim();
    // AR: dots are thousand separators, comma is decimal
    const normalized = match[1].replace(/\./g, '').replace(',', '.');
    const num = parseFloat(normalized);
    if (!isNaN(num) && num >= 100) return {
      num,
      raw
    };
  }
  match = text.match(usRegex);
  if (match) {
    const raw = match[0].trim();
    const normalized = match[1].replace(/,/g, '');
    const num = parseFloat(normalized);
    if (!isNaN(num)) return {
      num,
      raw
    };
  }
  match = text.match(simpleRegex);
  if (match) {
    const raw = match[0].trim();
    const normalized = match[1].replace(/[.,]/g, '');
    const num = parseFloat(normalized);
    if (!isNaN(num)) return {
      num,
      raw
    };
  }
  return null;
}
function scanPage(): Product[] {
  if (typeof document === 'undefined') return [];
  const products: Product[] = [];
  const seen = new Set<string>();
  // 1) Try JSON-LD structured data first
  try {
    const scripts = document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]');
    scripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || 'null');
        const items = Array.isArray(data) ? data : [data];
        items.forEach(item => {
          if (!item) return;
          if (item['@type'] === 'Product' && item.offers) {
            const offer = Array.isArray(item.offers) ? item.offers[0] : item.offers;
            const priceNumber = parseFloat(offer.price);
            if (!isNaN(priceNumber)) {
              const currency = offer.priceCurrency || 'ARS';
              const product: Product = {
                title: item.name || 'Producto',
                priceNumber,
                priceText: `$${priceNumber.toLocaleString('es-AR')} ${currency}`,
                thumbnailUrl: Array.isArray(item.image) ? item.image[0] : item.image
              };
              const key = `${product.title}-${product.priceNumber}`;
              if (!seen.has(key)) {
                seen.add(key);
                products.push(product);
              }
            }
          }
        });
      } catch {}
    });
  } catch {}
  // 2) Heuristic: find heading + price clusters
  if (products.length === 0) {
    const headings = document.querySelectorAll('h1, h2, h3');
    headings.forEach(h => {
      const title = h.textContent?.trim();
      if (!title || title.length < 4 || title.length > 120) return;
      // Look in the nearest section/article for price text
      const container = h.closest('section, article, div');
      if (!container) return;
      const text = container.textContent || '';
      const price = parsePrice(text);
      if (!price) return;
      // Skip if the title is a generic section title (e.g. "FAQ")
      const lower = title.toLowerCase();
      if (/preguntas|faq|qué|por qué|cómo|testimon/i.test(lower)) return;
      // Find image near it
      const img = container.querySelector<HTMLImageElement>('img');
      const product: Product = {
        title,
        priceNumber: price.num,
        priceText: price.raw + ' ARS',
        thumbnailUrl: img?.src
      };
      const key = `${product.title}-${product.priceNumber}`;
      if (!seen.has(key)) {
        seen.add(key);
        products.push(product);
      }
    });
  }
  // Filter out obviously wrong products (free items, ridiculously small prices)
  return products.filter(p => p.priceNumber >= 1000 && p.priceNumber <= 500000);
}
// ---------------------------------------------------------------------------
// Random helpers
// ---------------------------------------------------------------------------
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickWeighted<T>(items: {
  item: T;
  weight: number;
}[]): T {
  const total = items.reduce((acc, i) => acc + i.weight, 0);
  let r = Math.random() * total;
  for (const i of items) {
    r -= i.weight;
    if (r <= 0) return i.item;
  }
  return items[items.length - 1].item;
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function PurchaseToast() {
  const [currentToast, setCurrentToast] = useState<ToastData | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const reducedMotion = useRef(false);
  // Detect reduced motion preference
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotion.current = mq.matches;
    const handler = () => {
      reducedMotion.current = mq.matches;
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  // Scan products from DOM on mount (with a small delay so the page renders)
  useEffect(() => {
    const t = setTimeout(() => {
      const scanned = scanPage();
      setProducts(scanned);
    }, 800);
    return () => clearTimeout(t);
  }, []);
  // Pick a non-repeating name
  const pickName = () => {
    const history = nameHistoryRef.current;
    const available = NAMES.filter(n => !history.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pickRandom(pool);
    history.push(name);
    if (history.length > 10) history.shift();
    return name;
  };
  // Pick a product from scanned catalog or fallback
  const pickProduct = (): Product => {
    if (products.length > 0) {
      return pickRandom(products);
    }
    return pickWeighted(FALLBACK_PRODUCTS.map(p => ({
      item: p.product,
      weight: p.weight
    })));
  };
  // Schedule the toast loop: 3s initial delay, then 8-15s between toasts,
  // each toast visible 4s.
  useEffect(() => {
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout>;
    const showNext = () => {
      if (!mounted) return;
      const toast: ToastData = {
        id: ++idRef.current,
        name: pickName(),
        city: pickRandom(CITIES),
        product: pickProduct()
      };
      setCurrentToast(toast);
      // Hide after 4s
      timeoutId = setTimeout(() => {
        if (!mounted) return;
        setCurrentToast(null);
        // Schedule next toast after random 8-15s
        const nextDelay = 8000 + Math.random() * 7000;
        timeoutId = setTimeout(showNext, nextDelay);
      }, 4000);
    };
    // First toast after 3s
    timeoutId = setTimeout(showNext, 3000);
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);
  const animProps = reducedMotion.current ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.2
    }
  } : {
    initial: {
      opacity: 0,
      y: 60,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 40,
      scale: 0.95
    },
    transition: {
      type: 'spring' as const,
      stiffness: 260,
      damping: 26
    }
  };
  return <div aria-live="polite" aria-atomic="true" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] pointer-events-none w-full max-w-md px-4" data-id="element-375">
      <AnimatePresence mode="wait" data-id="element-376">
        {currentToast && <motion.div key={currentToast.id} {...animProps} className="pointer-events-auto bg-white rounded-2xl shadow-xl shadow-stone-900/15 border border-stone-200 px-4 py-3 flex items-center gap-3" data-id="element-377">
            {/* Thumbnail */}
            <div className="shrink-0 w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center overflow-hidden" data-id="element-378">
              {currentToast.product.thumbnailUrl ? <img src={currentToast.product.thumbnailUrl} alt="" className="w-full h-full object-cover" data-id="element-379" /> : <ShoppingBag className="w-6 h-6 text-emerald-600" data-id="element-380" />}
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0" data-id="element-381">
              <p className="text-sm text-stone-800 leading-snug" data-id="element-382">
                <strong className="font-semibold text-stone-900" data-id="element-383">
                  {currentToast.name}
                </strong>{' '}
                <span className="text-stone-500" data-id="element-384">de</span>{' '}
                <span className="text-stone-700" data-id="element-385">{currentToast.city}</span>
              </p>
              <p className="text-xs text-stone-500 leading-snug mt-0.5 truncate" data-id="element-386">
                acaba de comprar{' '}
                <span className="font-semibold text-emerald-700" data-id="element-387">
                  {currentToast.product.editionLabel || currentToast.product.title}
                </span>{' '}
                — {currentToast.product.priceText}
              </p>
            </div>

            {/* Pulse dot */}
            <div className="shrink-0 relative w-2 h-2" data-id="element-388">
              <span className="absolute inset-0 rounded-full bg-emerald-500" data-id="element-389" />
              <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75" data-id="element-390" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}